import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './HomePage.module.css';
import BottomNavBar from '../components/BottomNavBar';
import PieChart from '../components/PieChart';
import InstructionCard from '../components/InstructionCard';
import LivesDisplay from '../components/LivesDisplay';
import Button from '../components/Button';
import BudgetSection from '../components/BudgetSection';
import BudgetDisplay from '../components/BudgetDisplay';
import SpendingIndicator from '../components/SpendingIndicator';
import plaidService, { Transaction } from '../services/plaidService';
import { createManualTransaction, fetchManualTransactions, deleteManualTransaction } from '../services/transactionService';
import { usePlaidLink } from 'react-plaid-link';
import { addXP } from '../utils/questUtils';
import GoalModal from '../components/GoalModal';
import TrackSpendingModal from '../components/TrackSpendingModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { useAuth } from '../context/AuthContext';

const HomePage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [linkingAccount, setLinkingAccount] = useState<boolean>(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [categoryMatcherMap, setCategoryMatcherMap] = useState<Record<string, string>>({});
  const [monthlyBudget, setMonthlyBudget] = useState<number>(0);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isGoalModalOpen, setGoalModalOpen] = useState<boolean>(false);
  const [userGoal, setUserGoal] = useState<string | null>(null);
  const [yearlyIncomeBudget, setYearlyIncomeBudget] = useState<{
    monthly: number;
    needs: number;
    wants: number;
    savings: number;
  } | null>(null);
  const [spendingGuessData, setSpendingGuessData] = useState<{
    monthlyIncome: number;
    spendingAmount: number;
  } | null>(null);
  const [isExpenseModalOpen, setExpenseModalOpen] = useState<boolean>(false);
  const [manualTransactions, setManualTransactions] = useState<Transaction[]>([]);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);

  // Navigation and auth hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();



  // Handle API errors and redirect to welcome if not authenticated
  const handleApiError = useCallback((error: any) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  }, [navigate]);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await plaidService.getPersistedTransactions();
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  const refreshTransactions = async () => {
    try {
      setLoading(true);
      await plaidService.fetchTransactions();
      await fetchTransactions();
      addXP(5);
    } catch (error) {
      console.error('Error refreshing transactions:', error);
      handleApiError(error);
    }
  };

  const loadManualTransactions = useCallback(async () => {
    try {
      console.log('Loading manual transactions...');
      const data = await fetchManualTransactions();
      console.log('Manual transactions loaded:', data);
      setManualTransactions(data);
    } catch (error) {
      console.error('Error loading manual transactions:', error);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
    loadManualTransactions();

    // Check for URL refresh parameter (iOS compatibility)
    const searchParams = new URLSearchParams(location.search);
    const refreshParam = searchParams.get('refresh');
    if (refreshParam) {
      console.log('HomePage: Detected refresh URL parameter, force reloading transactions');
      // Clean up URL
      navigate('/home', { replace: true });
      // Force reload after a small delay
      setTimeout(() => {
        loadManualTransactions();
      }, 100);
    }

    // Check if we need to refresh transactions (set by AddExpenseButton)
    const needsRefresh = localStorage.getItem('needsTransactionRefresh');
    if (needsRefresh === 'true') {
      console.log('HomePage: Detected needsTransactionRefresh flag, refreshing manual transactions');
      loadManualTransactions();
      localStorage.removeItem('needsTransactionRefresh');
    }

    // Load category matcher results and monthly budget from localStorage
    const savedResults = localStorage.getItem('category_matcher_results');
    if (savedResults) {
      setCategoryMatcherMap(JSON.parse(savedResults));
    }

    const savedBudget = localStorage.getItem('budget_income');
    if (savedBudget) {
      setMonthlyBudget(parseFloat(savedBudget));
    }

    // Check if yearly income quest is completed and load budget data
    const yearlyIncomeQuestState = localStorage.getItem('quest_yearly-income');
    if (yearlyIncomeQuestState) {
      try {
        const questState = JSON.parse(yearlyIncomeQuestState);
        // Quest is completed if timeRemaining is 0 and isStarted is false
        if (questState.timeRemaining === 0 && !questState.isStarted) {
          const savedBudgetData = localStorage.getItem('yearly_income_budget');
          if (savedBudgetData) {
            const budgetData = JSON.parse(savedBudgetData);
            setYearlyIncomeBudget(budgetData);
          }
        }
      } catch (error) {
        console.error('Error parsing yearly income quest state:', error);
      }
    }

    // Check if spending guesstimate quest is completed and load spending data
    const spendingGuessQuestState = localStorage.getItem('quest_spending-guesstimate');
    if (spendingGuessQuestState) {
      try {
        const questState = JSON.parse(spendingGuessQuestState);
        // Quest is completed if timeRemaining is 0 and isStarted is false
        if (questState.timeRemaining === 0 && !questState.isStarted) {
          const monthlySpendingGuess = localStorage.getItem('monthly_spending_guess');
          const yearlyIncome = localStorage.getItem('yearly_income');

          if (monthlySpendingGuess && yearlyIncome) {
            const spendingAmount = parseInt(monthlySpendingGuess);
            const monthlyIncome = parseFloat(yearlyIncome) / 12;

            setSpendingGuessData({
              monthlyIncome,
              spendingAmount
            });
          }
        }
      } catch (error) {
        console.error('Error parsing spending guesstimate quest state:', error);
      }
    }

    // Listen for transaction updates (when user returns from Category Matcher)
    const handleTransactionsUpdated = () => {
      console.log('HomePage: Received transactionsUpdated event, refreshing data...');
      fetchTransactions();
    };

    // Listen for manual transaction additions (from AddExpenseButton)
    const handleManualTransactionAdded = () => {
      console.log('HomePage: Received manualTransactionAdded event, refreshing manual transactions...');
      loadManualTransactions();
    };

    window.addEventListener('transactionsUpdated', handleTransactionsUpdated);
    window.addEventListener('manualTransactionAdded', handleManualTransactionAdded);

    return () => {
      window.removeEventListener('transactionsUpdated', handleTransactionsUpdated);
      window.removeEventListener('manualTransactionAdded', handleManualTransactionAdded);
    };
  }, [fetchTransactions, loadManualTransactions, location.search, navigate]);

  useEffect(() => {
    // Only check goal when user is authenticated
    if (!isAuthenticated) return;
    
    const checkGoal = () => {
      const savedGoal = localStorage.getItem('userGoal');
      console.log('HomePage: Checking for saved goal:', savedGoal);
      console.log('HomePage: Current authentication state:', isAuthenticated);
      console.log('HomePage: Current user:', user);
      
      if (savedGoal) {
        console.log('HomePage: Found saved goal, setting userGoal to:', savedGoal);
        setUserGoal(savedGoal);
        setGoalModalOpen(false);
      } else {
        console.log('HomePage: No saved goal found, opening modal');
        setGoalModalOpen(true);
      }
    };
    
    // Check immediately and after a small delay for reliability
    checkGoal();
    const timeout1 = setTimeout(checkGoal, 100);
    const timeout2 = setTimeout(checkGoal, 500);
    const timeout3 = setTimeout(checkGoal, 1000);
    
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [isAuthenticated, user]);

  // Additional effect to check goal when component first mounts
  useEffect(() => {
    console.log('HomePage: Component mounted, checking for existing goal');
    const savedGoal = localStorage.getItem('userGoal');
    console.log('HomePage: Initial goal check:', savedGoal);
    
    if (savedGoal) {
      setUserGoal(savedGoal);
    }
  }, []);

  const handleSelectGoal = (goal: string) => {
    localStorage.setItem('userGoal', goal);
    setUserGoal(goal);
    setGoalModalOpen(false);
  };

  const handleExpenseComplete = async (amount: number, category?: string, description?: string) => {
    try {
      await createManualTransaction(amount, category || 'Want', description || 'Manual Expense');
      await loadManualTransactions();
      setExpenseModalOpen(false);
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Failed to save expense. Please try again.');
    }
  };

  const handleDeleteTransaction = (id: string | number) => {
    console.log('===== handleDeleteTransaction CALLED =====');
    console.log('handleDeleteTransaction called with id:', id, 'type:', typeof id);
    console.log('Current manualTransactions:', manualTransactions);

    // Find the transaction to delete
    const transaction = manualTransactions.find(t => t.id === id);
    if (!transaction) {
      console.error('Transaction not found:', id);
      console.log('Available transaction IDs:', manualTransactions.map(t => t.id));
      return;
    }

    console.log('Transaction found:', transaction);

    // Show the confirmation modal
    console.log('Setting transaction to delete and opening modal...');
    setTransactionToDelete(transaction);
    setDeleteModalOpen(true);
    console.log('Modal state updated, isDeleteModalOpen should now be true');
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;

    try {
      const numericId = typeof transactionToDelete.id === 'string'
        ? parseInt(transactionToDelete.id)
        : transactionToDelete.id;
      console.log('Attempting to delete transaction with numeric id:', numericId);

      await deleteManualTransaction(numericId);
      console.log('Delete successful, reloading manual transactions');

      await loadManualTransactions();
      console.log('Manual transactions reloaded after delete');

      // Close modal and clear state
      setDeleteModalOpen(false);
      setTransactionToDelete(null);
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Failed to delete transaction. Please try again.');
      setDeleteModalOpen(false);
      setTransactionToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setTransactionToDelete(null);
  };

  // Debug: Log when delete modal state changes
  useEffect(() => {
    console.log('Delete modal state changed:', {
      isDeleteModalOpen,
      transactionToDelete,
      transactionId: transactionToDelete?.id,
      transactionName: transactionToDelete?.name
    });
  }, [isDeleteModalOpen, transactionToDelete]);

  // Store the redirect URI when received on mobile
  const [receivedRedirectUri, setReceivedRedirectUri] = useState<string | null>(null);
  const [processingRedirect, setProcessingRedirect] = useState<boolean>(false);

  // Listen for mobile deep link redirects
  useEffect(() => {
    const handlePlaidRedirect = (event: CustomEvent) => {
      console.log('HomePage received Plaid redirect event:', event.detail);
      setProcessingRedirect(true);
      setReceivedRedirectUri(event.detail.url);
      // Clear processing state after a reasonable timeout if success doesn't trigger
      setTimeout(() => {
        setProcessingRedirect(false);
      }, 10000);
    };

    window.addEventListener('plaidRedirect', handlePlaidRedirect as EventListener);
    
    return () => {
      window.removeEventListener('plaidRedirect', handlePlaidRedirect as EventListener);
    };
  }, []);

  // Create Plaid Link configuration
  const config = {
    env: process.env.NODE_ENV === 'production' ? 'production' : 'sandbox',
    ...(receivedRedirectUri && { receivedRedirectUri }),
    onSuccess: async (publicToken: string) => {
      try {
        setProcessingRedirect(false); // Clear redirect processing state
        setLinkingAccount(true);
        setLinkError(null);
        console.log('Plaid Link success, exchanging token...');
        
        await plaidService.exchangePublicToken(publicToken);
        console.log('Token exchanged successfully');
        
        // Wait a bit for Plaid to process the connection
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        await refreshTransactions();
        addXP(25);
        
        // Set account master achievement
        if (!localStorage.getItem('account_master_unlocked')) {
          localStorage.setItem('account_master_unlocked', 'true');
          addXP(100); // Award 100 XP for Account Master achievement
          setTimeout(() => {
            alert('🏦 Achievement Unlocked: Account Master! You earned 100 XP for connecting your first account!');
          }, 1000);
        }
        
        setLinkingAccount(false);
      } catch (error: any) {
        console.error('Error during Plaid account linking:', error);
        setLinkError(error.message || 'Failed to connect bank account. Please try again.');
        setLinkingAccount(false);
      }
    },
    onError: (error: any) => {
      console.error('Plaid Link error:', error);
      setProcessingRedirect(false); // Clear redirect processing state
      setLinkError(error.display_message || 'An error occurred. Please try again.');
      setLinkingAccount(false);
    },
    onExit: (err: any, metadata: any) => {
      console.log('Plaid Link exited', { err, metadata });
      setProcessingRedirect(false); // Clear redirect processing state
      if (err && err.error_code) {
        setLinkError(`Error: ${err.display_message || err.error_message || 'Connection failed'}`);
      }
      setLinkingAccount(false);
    },
    token: linkToken,
  };

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    const createLinkToken = async () => {
      try {
        const response = await plaidService.createLinkToken();
        setLinkToken(response);
      } catch (error) {
        console.error('Error creating link token:', error);
        handleApiError(error);
      }
    };

    createLinkToken();
  }, [handleApiError]);

  // Calculate budget data for pie chart
  const calculateBudgetData = () => {
    if (!transactions.length) return [];

    const categories = { 'Need': 0, 'Want': 0, 'Savings': 0 };
    
    transactions.forEach(transaction => {
      // Use budget_category from database, fall back to localStorage mapping, then default to 'Need'
      const category = transaction.budget_category || categoryMatcherMap[transaction.id] || 'Need';
      if (transaction.amount > 0) {
        categories[category as keyof typeof categories] += transaction.amount;
      }
    });

    return [
      { label: 'Needs', value: categories.Need, color: '#ef4444' },
      { label: 'Wants', value: categories.Want, color: '#f59e0b' },
      { label: 'Savings', value: categories.Savings, color: '#10b981' }
    ];
  };

  const budgetData = calculateBudgetData();
  const totalSpending = budgetData.reduce((sum, item) => sum + item.value, 0);

  if (loading && !linkingAccount) {
    return (
      <div className={styles.homePage}>
        <GoalModal
          isOpen={isGoalModalOpen}
          onClose={() => setGoalModalOpen(false)}
          onSelectGoal={handleSelectGoal}
        />
        <div>Loading your financial dashboard...</div>
        <BottomNavBar />
      </div>
    );
  }

  return (
    <div className={styles.homePage} style={{ paddingBottom: 200 }}>
      <GoalModal
        isOpen={isGoalModalOpen}
        onClose={() => setGoalModalOpen(false)}
        onSelectGoal={handleSelectGoal}
      />
      <h1 className={styles.title}>WealthPlay Dashboard</h1>
      
      
      <LivesDisplay />
      
      {userGoal && (
        <div
          className={styles.userGoal}
          onClick={() => setGoalModalOpen(true)}
          style={{ cursor: 'pointer' }}
          title="Click to change your goal"
        >
          <strong>Your Goal:</strong> {userGoal}
        </div>
      )}

      {/* Enter Expense Button */}
      <div style={{ margin: '16px 0', textAlign: 'center' }}>
        <Button onClick={() => setExpenseModalOpen(true)}>
          Enter Expense
        </Button>
      </div>

      {/* Expense Modal */}
      <TrackSpendingModal
        isOpen={isExpenseModalOpen}
        onClose={() => setExpenseModalOpen(false)}
        onComplete={handleExpenseComplete}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        transactionName={transactionToDelete?.name || ''}
        amount={transactionToDelete?.amount || 0}
      />

      {/* Manual Transactions Section */}
      <div style={{ margin: '24px 0' }}>
        <h2 style={{ marginBottom: '16px' }}>Manual Transactions</h2>
        {manualTransactions.length > 0 ? (
          <div>
            {manualTransactions.map((transaction) => (
              <div key={transaction.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e5e7eb',
                background: '#fff',
                marginBottom: '4px',
                borderRadius: '4px'
              }}>
                <div>
                  <div style={{ fontWeight: '600' }}>{transaction.name}</div>
                  <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    {new Date(transaction.date).toLocaleDateString()}
                  </div>
                  {transaction.budget_category && (
                    <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 6px',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: transaction.budget_category === 'Need' ? '#fff7ed' :
                                       transaction.budget_category === 'Want' ? '#fef2f2' : '#f9fafb',
                        color: transaction.budget_category === 'Need' ? '#ea580c' :
                               transaction.budget_category === 'Want' ? '#dc2626' : '#6b7280'
                      }}>
                        {transaction.budget_category}
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    fontWeight: '600',
                    color: '#ef4444'
                  }}>
                    ${Math.abs(transaction.amount).toFixed(2)}
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Delete button clicked for transaction:', transaction.id);
                      handleDeleteTransaction(transaction.id);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Delete button touched for transaction:', transaction.id);
                      handleDeleteTransaction(transaction.id);
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
                      touchAction: 'manipulation',
                      minHeight: '44px',
                      minWidth: '60px',
                      userSelect: 'none',
                      WebkitUserSelect: 'none'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <p>No manual transactions yet. Click "Enter Expense" to add one!</p>
          </div>
        )}
      </div>

      {/* Show Spending Indicator if spending guesstimate quest is completed */}
      {spendingGuessData && (
        <SpendingIndicator
          spendingAmount={spendingGuessData.spendingAmount}
          monthlyIncome={spendingGuessData.monthlyIncome}
        />
      )}

      {/* Show 50/30/20 Budget Display if yearly income quest is completed */}
      {yearlyIncomeBudget && (
        <BudgetDisplay
          monthlyIncome={yearlyIncomeBudget.monthly}
          needs={yearlyIncomeBudget.needs}
          wants={yearlyIncomeBudget.wants}
          savings={yearlyIncomeBudget.savings}
        />
      )}

      {/* Show redirect processing status */}
      {processingRedirect && (
        <div style={{
          padding: '20px',
          margin: '20px 0',
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '1.1rem', color: '#92400e', marginBottom: '10px' }}>
            ⏳ Processing bank authentication...
          </p>
          <p style={{ color: '#78350f' }}>
            Please wait while we complete your account connection.
          </p>
        </div>
      )}
      
      {/* Show linking status */}
      {linkingAccount && (
        <div style={{
          padding: '20px',
          margin: '20px 0',
          backgroundColor: '#f0f9ff',
          border: '1px solid #3b82f6',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '1.1rem', color: '#1e40af', marginBottom: '10px' }}>
            🔄 Retrieving account information...
          </p>
          <p style={{ color: '#64748b' }}>
            Please wait while we connect to your bank account.
          </p>
        </div>
      )}
      
      {/* Show error if any */}
      {linkError && (
        <div style={{
          padding: '20px',
          margin: '20px 0',
          backgroundColor: '#fef2f2',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#dc2626', marginBottom: '10px' }}>
            ❌ {linkError}
          </p>
          <Button onClick={() => setLinkError(null)}>Dismiss</Button>
        </div>
      )}
      
      <InstructionCard
        title="Your Financial Overview"
        content={[
          "Welcome to your personalized financial dashboard! Here you can see your spending breakdown, recent transactions, and budget analysis.",
          "Connect your bank account to start tracking your expenses automatically and unlock powerful insights.",
          "Use the Category Matcher to classify your spending and see how you're doing against the 50/30/20 budget rule!"
        ]}
        emoji="💰"
        storageKey="home_instructions_dismissed"
      />

      {/* Budget Overview Section */}
      {monthlyBudget > 0 && (
        <div style={{ margin: '24px 0' }}>
          <BudgetSection
            title="Monthly Budget"
            amount={monthlyBudget}
            description="Your total monthly income budget"
          />
        </div>
      )}

      {/* Spending Pie Chart */}
      {budgetData.length > 0 && totalSpending > 0 ? (
        <div style={{ margin: '32px 0' }}>
          <PieChart 
            data={budgetData} 
            title="Spending Breakdown" 
            size={280}
            showBudgetAnalysis={true}
          />
          <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '1.1rem', fontWeight: '600' }}>
            Total Spending: ${totalSpending.toFixed(2)}
          </div>
        </div>
      ) : (
        <div style={{ 
          padding: '32px', 
          textAlign: 'center', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          margin: '24px 0'
        }}>
          <h3>No spending data available</h3>
          <p>Connect your bank account and categorize transactions to see your spending breakdown.</p>
        </div>
      )}

      {/* Recent Transactions Preview */}
      <div style={{ margin: '32px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2>Recent Transactions</h2>
          <Button onClick={() => navigate('/transactions')}>View All</Button>
        </div>
        
        {transactions.length > 0 ? (
          <div>
            {transactions.slice(0, 5).map((transaction) => (
              <div key={transaction.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #e5e7eb',
                background: '#fff',
                marginBottom: '4px',
                borderRadius: '4px'
              }}>
                <div>
                  <div style={{ fontWeight: '600' }}>{transaction.name}</div>
                  <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    {Array.isArray(transaction.category) 
                      ? transaction.category.join(' > ') 
                      : transaction.category || 'Uncategorized'}
                  </div>
                  {transaction.budget_category && transaction.budget_category !== 'uncategorized' && (
                    <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 6px',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: transaction.budget_category === 'Need' ? '#fff7ed' :
                                       transaction.budget_category === 'Want' ? '#fef2f2' :
                                       transaction.budget_category === 'Savings' ? '#f0fdf4' :
                                       transaction.budget_category === 'Income' ? '#f0f9ff' : '#f9fafb',
                        color: transaction.budget_category === 'Need' ? '#ea580c' :
                               transaction.budget_category === 'Want' ? '#dc2626' :
                               transaction.budget_category === 'Savings' ? '#16a34a' :
                               transaction.budget_category === 'Income' ? '#2563eb' : '#6b7280'
                      }}>
                        {transaction.budget_category}
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ 
                  fontWeight: '600',
                  color: transaction.amount < 0 ? '#10b981' : '#ef4444'
                }}>
                  ${Math.abs(transaction.amount).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <p>No transactions found. Connect your bank account to get started!</p>
            {ready && (
              <Button onClick={() => open()}>Connect Bank Account</Button>
            )}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div style={{ margin: '32px 0' }}>
        <h2>Quick Actions</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginTop: '16px' }}>
          <Button onClick={refreshTransactions}>Refresh Data</Button>
        </div>
      </div>

      <BottomNavBar />
    </div>
  );
};

export default HomePage;